import { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ImagePlus, X, Loader2, FileText, Upload } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";

const ScheduleNotesSection = ({
  expectedStartDate,
  onExpectedStartDateChange,
  startDateType,
  onStartDateTypeChange,
  validDays,
  onValidDaysChange,
  notes,
  onNotesChange,
  notesImage,
  onNotesImageChange,
  quoteId,
  quotePdfFilename,
  onQuotePdfChange,
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const fileInputRef = useRef(null);
  const pdfInputRef = useRef(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be less than 5MB"); return; }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      onNotesImageChange(response.data.url);
      toast.success("Image uploaded");
    } catch (error) {
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Please select a PDF file"); return; }
    if (file.size > 20 * 1024 * 1024) { toast.error("PDF must be less than 20MB"); return; }

    if (!quoteId) {
      toast.error("Please save the quote first before uploading a PDF");
      return;
    }

    setUploadingPdf(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(`${API}/quotes/${quoteId}/upload-pdf`, formData);
      onQuotePdfChange(file.name);
      toast.success("PDF uploaded successfully!");
    } catch (error) {
      console.error("PDF upload error:", error.response?.data || error.message);
      toast.error(`Failed to upload PDF: ${error.response?.data?.detail || error.message}`);
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleRemovePdf = async () => {
    if (!quoteId) return;
    try {
      await axios.delete(`${API}/quotes/${quoteId}/pdf`);
      onQuotePdfChange(null);
      if (pdfInputRef.current) pdfInputRef.current.value = "";
      toast.success("PDF removed");
    } catch {
      toast.error("Failed to remove PDF");
    }
  };

  return (
    <Card className="bg-white border border-zinc-200 shadow-sm rounded-sm">
      <CardHeader className="border-b border-zinc-100 px-6 py-4">
        <CardTitle className="font-heading text-lg uppercase tracking-tight text-[#00341d]">Schedule, Notes & Attachments</CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        <div>
          <Label className="text-sm font-medium text-zinc-700 mb-2 block">Start Date</Label>
          <div className="flex gap-4 mb-3" role="radiogroup" aria-label="Start date type">
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input
                type="radio"
                name="start-date-type"
                value="first_available"
                checked={startDateType === "first_available"}
                onChange={() => { onStartDateTypeChange("first_available"); onExpectedStartDateChange(""); }}
                className="accent-[#00341d]"
                data-testid="start-date-type-first-available"
              />
              <span>First available date</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input
                type="radio"
                name="start-date-type"
                value="preferred"
                checked={startDateType === "preferred"}
                onChange={() => onStartDateTypeChange("preferred")}
                className="accent-[#00341d]"
                data-testid="start-date-type-preferred"
              />
              <span>Preferred start date</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input
                type="radio"
                name="start-date-type"
                value="valid_for"
                checked={startDateType === "valid_for"}
                onChange={() => { onStartDateTypeChange("valid_for"); onExpectedStartDateChange(""); if (!validDays) onValidDaysChange?.(30); }}
                className="accent-[#00341d]"
                data-testid="start-date-type-valid-for"
              />
              <span>Quote valid for X days (no start date)</span>
            </label>
          </div>
          {startDateType === "preferred" && (
            <Input
              type="date"
              value={expectedStartDate}
              onChange={(e) => onExpectedStartDateChange(e.target.value)}
              className="bg-zinc-50/50"
              data-testid="expected-start-date-input"
            />
          )}
          {startDateType === "valid_for" && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-zinc-700">Quote valid for</span>
              <Input
                type="number"
                min="1"
                value={validDays ?? ""}
                onChange={(e) => onValidDaysChange?.(e.target.value === "" ? null : (parseInt(e.target.value, 10) || 0))}
                className="w-24 bg-zinc-50/50"
                data-testid="valid-days-input"
              />
              <span className="text-sm text-zinc-700">days</span>
            </div>
          )}
          <p className="text-xs text-zinc-500 mt-2 leading-relaxed">
            {startDateType === "valid_for"
              ? "The quote will not mention a start date — it will show that this quote is valid for the number of days above."
              : "Start dates are estimates and may shift due to weather, equipment availability, or completion of work for prior clients. We'll make every effort to honor your preferred schedule."}
          </p>
        </div>
        <div>
          <Label className="text-sm font-medium text-zinc-700 mb-2 block">Additional Notes</Label>
          <Textarea value={notes} onChange={(e) => onNotesChange(e.target.value)} placeholder="Add any special instructions, access details, or project-specific requirements..." rows={4} className="bg-zinc-50/50 resize-none" data-testid="notes-textarea" />
        </div>
        <div>
          <Label className="text-sm font-medium text-zinc-700 mb-2 block">Attach Image (Optional)</Label>
          {notesImage ? (
            <div className="relative inline-block">
              <img src={notesImage} alt="Notes attachment" className="max-w-xs max-h-48 rounded-lg border border-zinc-200 object-cover" />
              <button onClick={() => { onNotesImageChange(null); if (fileInputRef.current) fileInputRef.current.value = ""; }} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 shadow-md" type="button">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" id="notes-image-upload" />
              <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="border-dashed">
                {uploading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>) : (<><ImagePlus className="w-4 h-4 mr-2" />Add Image</>)}
              </Button>
              <p className="text-xs text-zinc-500 mt-1">PNG, JPG up to 5MB</p>
            </div>
          )}
        </div>
        <div>
          <Label className="text-sm font-medium text-zinc-700 mb-2 block">Detailed Quote PDF</Label>
          <p className="text-xs text-zinc-500 mb-2">Upload a detailed PDF that customers can download from the quote page</p>
          {quotePdfFilename ? (
            <div className="flex items-center gap-3 bg-[#00341d]/5 border border-[#00341d]/20 rounded-lg p-3">
              <FileText className="w-5 h-5 text-[#00341d] flex-shrink-0" />
              <span className="text-sm font-medium text-[#00341d] truncate flex-1">{quotePdfFilename}</span>
              <button onClick={handleRemovePdf} className="text-red-500 hover:text-red-700 p-1" type="button">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div>
              <input ref={pdfInputRef} type="file" accept="application/pdf" onChange={handlePdfUpload} className="hidden" id="quote-pdf-upload" />
              <Button type="button" variant="outline" onClick={() => pdfInputRef.current?.click()} disabled={uploadingPdf || !quoteId} className="border-dashed" data-testid="upload-pdf-button">
                {uploadingPdf ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>) : (<><Upload className="w-4 h-4 mr-2" />Upload PDF</>)}
              </Button>
              {!quoteId && <p className="text-xs text-amber-600 mt-1">Save the quote first to enable PDF upload</p>}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ScheduleNotesSection;
